/**
 * Loads and validates the generated brand/model menu asset (menu-<market>.json),
 * shared by the homepage device finder (custom-product-finder.liquid) and the
 * in-collection device filter picker (collection-device-filter.liquid).
 *
 * @param {string} assetUrl
 * @param {string} expectedMarket
 * @returns {{loadLevel: function(string): Promise<object>}}
 */
function createMenuAssetLoader(assetUrl, expectedMarket) {
  var cache = Object.create(null);
  var assetDataPromise = null;
  var assetWarningLogged = false;
  var menuFetchTimeoutMs = 10000;

  function fetchMenuJson(url, options, errorLabel) {
    var controller = new AbortController();
    var timeoutId = window.setTimeout(function () {
      controller.abort();
    }, menuFetchTimeoutMs);

    return fetch(url, Object.assign({}, options, { signal: controller.signal }))
      .then(function (response) {
        if (!response.ok) throw new Error(errorLabel + ': ' + response.status);
        return response.json();
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          throw new Error(errorLabel + ': timed out');
        }
        throw error;
      })
      .finally(function () {
        window.clearTimeout(timeoutId);
      });
  }

  function warnAssetFailure(error) {
    if (assetWarningLogged) return;
    assetWarningLogged = true;
    if (window.console && typeof window.console.warn === 'function') {
      window.console.warn('Generated menu asset could not be loaded.', error);
    }
  }

  function validateAssetData(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Menu asset was not an object');
    }
    if (data.version !== 1) {
      throw new Error('Unsupported menu asset version: ' + String(data.version));
    }
    if (data.placeholder) {
      throw new Error('Menu asset is placeholder');
    }
    if (!data.market || data.market.key !== expectedMarket) {
      throw new Error('Menu asset market mismatch: expected ' + expectedMarket);
    }
    if (data.rootHandle !== '__root') {
      throw new Error('Menu asset root mismatch: expected __root');
    }
    if (!data.levels || typeof data.levels !== 'object' || Array.isArray(data.levels)) {
      throw new Error('Menu asset did not include a valid levels object');
    }

    return data;
  }

  function validateAssetChild(child, levels) {
    if (!child || typeof child !== 'object' || Array.isArray(child)) {
      throw new Error('Menu asset included an invalid child');
    }
    if (!child.handle || typeof child.handle !== 'string') {
      throw new Error('Menu asset child did not include a handle');
    }
    if (typeof child.title !== 'string' || typeof child.url !== 'string' || typeof child.type !== 'string') {
      throw new Error('Menu asset child included invalid text fields: ' + child.handle);
    }
    if (child.image !== null && child.image !== undefined && typeof child.image !== 'string') {
      throw new Error('Menu asset child included an invalid image: ' + child.handle);
    }
    if (
      typeof child.hasChildren !== 'boolean' ||
      !Number.isInteger(child.childCount) ||
      child.childCount < 0 ||
      child.hasChildren !== (child.childCount > 0)
    ) {
      throw new Error('Menu asset child included invalid child metadata: ' + child.handle);
    }
    if (child.hasChildren) {
      var childLevel = levels[child.handle];
      if (
        !Object.prototype.hasOwnProperty.call(levels, child.handle) ||
        !childLevel ||
        typeof childLevel !== 'object' ||
        Array.isArray(childLevel) ||
        !Array.isArray(childLevel.children) ||
        childLevel.children.length !== child.childCount
      ) {
        throw new Error('Menu asset omitted child level: ' + child.handle);
      }
    }
  }

  function levelFromAsset(data, handle) {
    var levels = data.levels;
    if (!Object.prototype.hasOwnProperty.call(levels, handle)) {
      throw new Error('Menu asset did not include handle: ' + handle);
    }

    var level = levels[handle];
    if (
      !level ||
      typeof level !== 'object' ||
      Array.isArray(level) ||
      level.handle !== handle ||
      !Array.isArray(level.children)
    ) {
      throw new Error('Menu asset included an invalid level: ' + handle);
    }
    level.children.forEach(function (child) {
      validateAssetChild(child, levels);
    });

    return level;
  }

  function loadAssetData() {
    if (assetDataPromise) return assetDataPromise;

    assetDataPromise = fetchMenuJson(
      assetUrl,
      { cache: 'force-cache', headers: { accept: 'application/json' } },
      'Menu asset request failed'
    )
      .then(function (data) {
        return validateAssetData(data);
      })
      .catch(function (error) {
        assetDataPromise = null;
        throw error;
      });

    return assetDataPromise;
  }

  function loadLevel(handle) {
    if (!handle) return Promise.reject(new Error('Missing menu handle'));
    if (Object.prototype.hasOwnProperty.call(cache, handle)) return Promise.resolve(cache[handle]);
    if (!assetUrl) return Promise.reject(new Error('Missing menu asset'));

    return loadAssetData()
      .then(function (data) {
        return levelFromAsset(data, handle);
      })
      .catch(function (error) {
        warnAssetFailure(error);
        throw error;
      })
      .then(function (data) {
        cache[handle] = data;
        return data;
      });
  }

  return { loadLevel: loadLevel };
}

window.createMenuAssetLoader = createMenuAssetLoader;
